import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Link from 'facet-rest/src/domain/Link';
import classNames from 'classnames';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Alert from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Alert';
import { statusTypes } from 'Components/ComponentLibrary/Components/Uncategorized/StatusItem';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import DialogContent from '@material-ui/core/DialogContent';
import Dialog from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Dialog';
import Slide from '@mui/material/Slide';
import useMobileLayout from 'Hooks/useMobileLayout';
import CloseIcon from '@material-ui/icons/Close';
import IconButton from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/IconButton';
import TextListCard from 'Components/ComponentLibrary/Molecules/TextListCard';
import { DS_SIZE_MAP } from 'Components/ComponentLibrary/DesignSystem/sizes';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';
import fr from 'facet-rest';
import { buildAdvisorLink } from 'Views/Page/Content/Templates';

const AIR_ACTION_ITEM_IMG = 'https://static.facetwealth.com/app/image/annual-checkin/dashboard-air-entry-item-screenshot.png';

const styles = theme => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    cursor: 'pointer',
  },
  alertMessage: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  title: {
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.primary.main,
    marginTop: theme.spacing(5),
    marginRight: theme.spacing(5),
    marginLeft: theme.spacing(5),
    marginBottom: 25,
  },
  dialogTitleMobile: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginLeft: theme.spacing(3),
  },
  dialogTitleText: {
    fontSize: theme.typography.h3.fontSize,
  },
  dialogCloseButton: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2),
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: 0,
    marginLeft: theme.spacing(5),
    marginRight: theme.spacing(5),
    marginBottom: theme.spacing(5),

  },
  dialogContentMobile: {
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  dialogContentText: {
    color: theme.palette.facet.primaryGray,
    fontSize: theme.typography.h5.fontSize,
    textAlign: 'center',
    marginBottom: 25,
  },
  airDialogContentSecondary: {
    maxWidth: 480,
  },
  airDialogImageContainer: {
    borderRadius: theme.radius.md,
    overflow: 'hidden',
    maxWidth: theme.dimensions.sm,
    marginBottom: 25,
    '& > img': {
      maxWidth: '100%',
      objectFit: 'contain',
    },
  },
  textListCard: {
    marginBottom: 25,
    maxWidth: theme.dimensions.sm,
    width: '100%',
  },
});

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

function AnnualReviewCheckInDialogInner({
  onClose,
  classes,
}) {
  const isMobile = useMobileLayout();

  return (
    <>
      <div className={classNames(classes.dialogTitle, isMobile && classes.dialogTitleMobile)}>
        <Typography className={classes.dialogTitleText}>Annual Check In</Typography>
        <IconButton onClick={onClose} icon={CloseIcon} className={classes.dialogCloseButton} />
      </div>
      <DialogContent className={classNames(classes.dialogContent, isMobile && classes.dialogContentMobile)}>
        <Typography className={classes.dialogContentText}>Once a year, for compliance we require that your client completes an annual check in that covers the following topics:</Typography>
        <TextListCard
          className={classes.textListCard}
          textArray={[
            'Update basic information',
            'Update risk tolerance',
            'Update investment objectives',
          ]}
        />
        <Typography className={classNames(classes.dialogContentText, classes.airDialogContentSecondary)}>
          Please remind your clients to complete the check-in as soon as possible. They can access it via the alert tile on their client dashboard, and it should take most clients
          roughly 10 minutes to complete.
        </Typography>
        <div className={classes.airDialogImageContainer}><img src={AIR_ACTION_ITEM_IMG} alt="Client dashboard AIR action item" /></div>
        <RoundedButton
          onClick={onClose}
          disableElevation
          variant="contained"
          color={'primary'}
          fullWidth
          size={DS_SIZE_MAP.sm}
        >
          Okay
        </RoundedButton>
      </DialogContent>
    </>
  );
}

function StatusBanner({
  resourceLink,
  classes,
  className,
  test,
}) {
  const isMobile = useMobileLayout();

  const [loading, setLoading] = useState(true);
  const [statusBanner, setStatusBanner] = useState(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const closeDialog = () => setDialogOpen(false);
  const openDialog = () => setDialogOpen(true);

  useEffect(() => {
    async function fetchData() {
      if (resourceLink) {
        const [data] = await resourceLink.get();
        setStatusBanner(data);
      }
    }

    fetchData().finally(() => {
      setLoading(false);
    });
  }, []);

  const severity = useMemo(() => {
    if (!statusBanner) return 'info';

    switch (statusBanner.attr('status')) {
      case statusTypes.ERROR:
        return 'error';
      case statusTypes.WARN:
        return 'warning';
      case statusTypes.SUCCESS:
        return 'success';
      case statusTypes.INFO:
        return 'info';
      default:
        return 'info';
    }
  }, [statusBanner]);

  const dialogInner = useMemo(() => {
    if (!statusBanner) return null;

    if (statusBanner.attr('resourceType') === 'AnnualInvestmentReviewProgress') {
      return <AnnualReviewCheckInDialogInner onClose={closeDialog} classes={classes} />;
    }

    return null;
  }, [statusBanner]);

  function handleClick() {
    if (dialogInner && statusBanner.attr('resourceType') === 'AnnualInvestmentReviewProgress') {
      openDialog();
    }

    if (statusBanner.attr('resourceType') === 'ManagedAccountModelUpdateRequestPage') {
      if (statusBanner.relationship(fr.relationships.managedAccountModelUpdateRequests.name)) {
        const url = buildAdvisorLink(statusBanner.relationship(fr.relationships.managedAccountModelUpdateRequests.name).link('related').url());
        window.open(url, '_blank');
      }
    }
  }

  if (!loading) {
    if (statusBanner) {
      return (
        <>
          <Alert
            test={test}
            onClick={handleClick}
            className={classNames(classes.root, className)}
            severity={severity}
            classes={{
              message: classes.alertMessage,
            }}
          >
            <Typography className={classes.title} test="status-banner-title">{statusBanner.attr('title')}</Typography>
            <ArrowRightIcon />
          </Alert>
          <Dialog
            anchor="bottom"
            open={dialogOpen}
            onClose={closeDialog}
            fullScreen={isMobile}
            TransitionComponent={Transition}
          >
            {dialogInner}
          </Dialog>
        </>
      );
    }
  }

  return null;
}
StatusBanner.propTypes = {
  resourceLink: PropTypes.instanceOf(Link),
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  test: PropTypes.string,
};
StatusBanner.defaultProps = {
  className: undefined,
  test: 'status-banner',
};

export default withStyles(styles)(StatusBanner);
